<script lang="ts">
    import type {RecordSearchParams} from 'src/typings';
    import {Kind, SearchType, ViewFormat} from 'shared/constants';
    import {mapFieldTypeIdToFilterField} from 'src/features/search/kp-search-field-type-filter';
    import {Flex} from 'shared/layouts';
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {LogbookReportPairingType} from './types';
    import {getProjectLogbookPairingContext} from './project-logbook-pairing-context';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {getDateFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
    import {hasOriginRecordReference} from 'src/features/record-grid/lib/types-utils';
    import {fade} from 'svelte/transition';
    import {FOND_INSTALLATION_LOGBOOK, FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
    import {logbookReportsPageSize} from './types';
    import KpBarebonesTable from 'shared/ui-widgets/table/barebones/KpBarebonesTable.svelte';
    import KpPageableSearchResults from 'src/features/search/kp-search-results/KpPageableSearchResults.svelte';
    import KpSearchContext from 'src/features/search/kp-search-context/KpSearchContext.svelte';
    import KpSearchToolbar from 'src/features/search/kp-search-toolbar/KpSearchToolbar.svelte';
    import LogbookReportItem from './LogbookReportItem.svelte';
    import TableRecordLinkHeader from 'shared/ui-widgets/table/barebones/TableRecordLinkHeader.svelte';

    export let logbookRecord: RecordRow;
    export let pairingType: LogbookReportPairingType;

    const context = getProjectLogbookPairingContext();

    const logbookDateField = getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.dateFieldId);
    const logbookCustomerProjectField = getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.customerProjectFieldId);
    const logbookDate = getDateFromGridFieldValue(logbookDateField);
    const processedReports$ = context.processedReports$;

    function createStaticParams(): RecordSearchParams {
        return {
            kind: [Kind.KIND_RECORD],
            type: SearchType.TYPE_GLOBAL,
            viewFormat: ViewFormat.TABLE_ROW,
            rootFond: [{id: FOND_REPORT.fond}],
            fond: FOND_REPORT.subfondsForLogbookPairing.map((fondId) => ({id: fondId})),
            pageSize: logbookReportsPageSize,
            qt: JSON.stringify(pairingType === 'unpair' ? createPairedConjunction() : createUnpairedConjunction())
        };
    }

    function createPairedConjunction() {
        return {
            and: [
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.installationLogbookFieldId, 'link'),
                    eq: {
                        value: logbookRecord.id
                    }
                }
            ]
        };
    }

    function createUnpairedConjunction() {
        let customerProjectId = context.projectRecord.id;

        if (hasOriginRecordReference(logbookCustomerProjectField)) {
            customerProjectId = logbookCustomerProjectField.originRecordReference.id;
        }

        return {
            and: [
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.dateFieldId, 'val'),
                    eq: {
                        value: logbookDate.toISODate()
                    }
                },
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.closestProjectFieldId, 'origin'),
                    eq: {
                        value: context.projectRecord.id
                    }
                },
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.customerProjectMainFieldId, 'link'),
                    eq: {
                        value: customerProjectId
                    }
                },
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.installationLogbookFieldId, 'link'),
                    present: {
                        value: false
                    }
                },
                {
                    not: {
                        field: mapFieldTypeIdToFilterField(FOND_REPORT.installationLogbookFieldId, 'link'),
                        eq: {
                            value: logbookRecord.id
                        }
                    }
                }
            ]
        };
    }
</script>

<Flex direction="column" gap="xxl" fillAvailableSpace>
    <KpSearchContext staticParams={createStaticParams()} localSearch let:searchManager>
        <KpSearchToolbar/>

        <KpPageableSearchResults pagination={searchManager.getPagination()} let:paginationData>
            <KpBarebonesTable fontSize="12px">
                <tr slot="header">
                    <TableRecordLinkHeader/>

                    {#if pairingType === 'pair'}
                        <th class="button-header"></th>
                    {/if}

                    <th>Pracovník/Stroj</th>
                    <th>Datum</th>
                    <th>Vykázáno</th>
                    <th>Účtováno</th>
                    <th>Typ</th>

                    {#if pairingType === 'pair'}
                        <th>Stav</th>
                    {/if}

                    {#if pairingType === 'unpair'}
                        <th class="button-header"></th>
                    {/if}
                </tr>

                <svelte:fragment slot="body">
                    {#each paginationData.items as record(record.id)}
                        <tr transition:fade={{duration: 250}}>
                            <LogbookReportItem {record}
                                               {pairingType}
                                               isProcessing={$processedReports$.includes(record.id)} on:pair-unpair/>
                        </tr>
                    {/each}
                </svelte:fragment>
            </KpBarebonesTable>
        </KpPageableSearchResults>
    </KpSearchContext>
</Flex>

<style lang="less">
    .button-header {
        width: 70px;
    }
</style>
